import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getMyMusicianProfile, updateMyMusicianProfile, updateMyGear } from '../lib/api';
import GearEditor from '../components/GearEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar, Save, X, Plus, AlertCircle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { validatePhone } from '../lib/utils';
import { RiderUpload } from '../components/RiderUpload';
import { useAuth } from '../context/AuthContext';
import { EXPERIENCE, RATE_TYPES } from '../constants/profileContract';

// Client-side host validation for video links (YouTube/Vimeo). No network.
function videoHostValid(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return ['youtube.com', 'm.youtube.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'].includes(host);
  } catch (e) {
    return false;
  }
}

export default function MyMusicianProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [displayName, setDisplayName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('NL');
  const [phone, setPhone] = useState('');
  const [shortBio, setShortBio] = useState('');
  const [primaryInstrument, setPrimaryInstrument] = useState('');
  const [instruments, setInstruments] = useState([]);
  const [genres, setGenres] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [rateType, setRateType] = useState('');
  const [baseRate, setBaseRate] = useState('');
  const [travelRadiusKm, setTravelRadiusKm] = useState('');
  const [beyondRadiusRatePerKm, setBeyondRadiusRatePerKm] = useState('');
  const [languages, setLanguages] = useState([]);
  const [gearList, setGearList] = useState([]);
  const [gearProfile, setGearProfile] = useState([]);
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [videos, setVideos] = useState([]);

  // Tag inputs
  const [newInstrument, setNewInstrument] = useState('');
  const [newGenre, setNewGenre] = useState('');
  const [newEventType, setNewEventType] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newGear, setNewGear] = useState('');
  const [newVideo, setNewVideo] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyMusicianProfile();
      const p = response.data;
      setProfile(p);
      setDisplayName(p.displayName || '');
      setCity(p.city || '');
      setCountry(p.country || 'NL');
      setPhone(p.phone || '');
      setShortBio(p.shortBio || '');
      setPortfolioUrl(p.portfolioUrl || '');
      setPrimaryInstrument(p.primaryInstrument || '');
      setInstruments(p.instruments || []);
      setGenres(p.genres || []);
      setExperienceLevel(p.musicianExperienceLevel || '');
      setEventTypes(p.eventTypes || []);
      setRateType(p.onboardingRateType || '');
      setBaseRate(p.baseRate?.toString() || '');
      setTravelRadiusKm(p.travelRadiusKm?.toString() || '');
      setBeyondRadiusRatePerKm(p.beyondRadiusRatePerKm?.toString() || '');
      setLanguages(p.languages || []);
      setGearList(p.gearList || []);
      setGearProfile(p.gear_profile || []);
      setVideos(p.videos || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!displayName || !city || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const phoneError = validatePhone(phone);
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    const badVideo = videos.find((v) => !videoHostValid(v));
    if (badVideo) {
      toast.error('Videos must be YouTube or Vimeo links');
      return;
    }

    setSaving(true);
    try {
      await updateMyMusicianProfile({
        displayName,
        city,
        country,
        phone,
        shortBio,
        portfolioUrl: portfolioUrl || null,
        primaryInstrument: primaryInstrument || null,
        instruments,
        genres,
        musicianExperienceLevel: experienceLevel || null,
        eventTypes,
        onboardingRateType: rateType || null,
        baseRate: baseRate ? parseFloat(baseRate) : null,
        currency: 'EUR',
        travelRadiusKm: travelRadiusKm ? parseInt(travelRadiusKm) : null,
        beyondRadiusRatePerKm: beyondRadiusRatePerKm ? parseFloat(beyondRadiusRatePerKm) : null,
        languages,
        gearList,
        // TODO(backend): persist `videos[]` — not yet on MusicianProfileUpdate /
        // DIRECTORY_ALLOWED_FIELDS, so the API silently drops this until it lands.
        videos,
      });
      await updateMyGear(gearProfile);
      toast.success('Profile saved!');
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addTag = (value, list, setList, setInput, max) => {
    if (!value.trim()) return;
    if (list.length >= max) {
      toast.error(`Maximum ${max} items allowed`);
      return;
    }
    if (!list.includes(value.trim())) {
      setList([...list, value.trim()]);
    }
    setInput('');
  };

  const removeTag = (value, list, setList) => {
    setList(list.filter((v) => v !== value));
  };

  if (loading) {
    return (
      <div className="content-container">
        <div className="h-64 skeleton-pulse"></div>
      </div>
    );
  }

  const isComplete = profile?.isComplete;

  const REQUIRED_FIELDS = [
    { key: 'displayName', label: 'Display name', value: displayName },
    { key: 'city', label: 'City', value: city },
    { key: 'phone', label: 'Phone', value: phone },
    { key: 'primaryInstrument', label: 'Primary instrument', value: primaryInstrument },
    { key: 'musicianExperienceLevel', label: 'Experience level', value: experienceLevel },
    { key: 'onboardingRateType', label: 'Rate type', value: rateType },
    { key: 'baseRate', label: 'Base rate', value: baseRate },
    { key: 'travelRadiusKm', label: 'Travel radius', value: travelRadiusKm },
  ];
  const missingFields = REQUIRED_FIELDS.filter(f => !f.value);

  return (
    <div className="content-container pb-32 md:pb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title gold-accent">My Profile</h1>
          <div className="flex items-center gap-2 mt-2">
            {isComplete ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            ) : (
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">
                <AlertCircle className="h-3 w-3 mr-1" />
                Incomplete
              </Badge>
            )}
          </div>
        </div>
        <Link to="/musicians/me/availability">
          <Button variant="outline" className="border-border bg-secondary hover:bg-secondary/80" data-testid="availability-link-btn">
            <Calendar className="h-4 w-4 mr-2" />
            Availability
          </Button>
        </Link>
      </div>

      {!isComplete && missingFields.length > 0 && (
        <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm">
          <p className="text-orange-400 font-medium mb-1.5">Complete your profile to appear in the directory</p>
          <p className="text-orange-400/70">Missing: {missingFields.map(f => f.label).join(', ')}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Info */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-foreground">Display Name *</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your stage name"
                  className="h-12 bg-secondary border-border"
                  data-testid="display-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground">Phone (WhatsApp) *</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+31612345678"
                  className="h-12 bg-secondary border-border"
                  data-testid="phone-input"
                />
                <p className="text-xs text-muted-foreground">Include country code, e.g. +31612345678. Never shown publicly.</p>
                {phone && validatePhone(phone) && (
                  <p className="text-xs text-red-400">{validatePhone(phone)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="text-foreground">City *</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Amsterdam"
                  className="h-12 bg-secondary border-border"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country" className="text-foreground">Country</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="h-12 bg-secondary border-border" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NL">Netherlands</SelectItem>
                    <SelectItem value="BE">Belgium</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shortBio" className="text-foreground">Short Bio</Label>
              <Textarea
                id="shortBio"
                value={shortBio}
                onChange={(e) => setShortBio(e.target.value)}
                placeholder="Tell venues about yourself..."
                maxLength={500}
                rows={3}
                className="bg-secondary border-border"
                data-testid="short-bio-input"
              />
              <p className="text-xs text-muted-foreground">{shortBio.length}/500</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="portfolioUrl" className="text-foreground">Portfolio URL</Label>
              <Input
                id="portfolioUrl"
                type="url"
                value={portfolioUrl}
                onChange={(e) => setPortfolioUrl(e.target.value)}
                placeholder="https://soundcloud.com/yourname"
                className="h-12 bg-secondary border-border"
                data-testid="portfolio-url-input"
              />
              <p className="text-xs text-muted-foreground">Link to your work (SoundCloud, YouTube, website, etc.)</p>
            </div>

            {/* Videos — showcase links. TODO(backend): `videos[]` not yet persisted
                (not on MusicianProfileUpdate / DIRECTORY_ALLOWED_FIELDS); the saved
                value is dropped until the field lands. Host-validated on save. */}
            <div className="space-y-2">
              <Label className="text-foreground">Videos (max 4)</Label>
              <p className="text-xs text-muted-foreground">Paste YouTube or Vimeo links. Checked when you save.</p>
              <div className="flex gap-2">
                <Input
                  value={newVideo}
                  onChange={(e) => setNewVideo(e.target.value)}
                  placeholder="Paste YouTube or Vimeo link"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newVideo, videos, setVideos, setNewVideo, 4)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newVideo, videos, setVideos, setNewVideo, 4)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-col gap-1.5">
                {videos.map((vurl) => (
                  <Badge key={vurl} variant="secondary" className="gap-1 bg-secondary border-border justify-between max-w-full">
                    <span className="truncate">{vurl}</span>
                    <X className="h-3 w-3 cursor-pointer shrink-0" onClick={() => removeTag(vurl, videos, setVideos)} />
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Info */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Professional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryInstrument" className="text-foreground">Primary Instrument *</Label>
                <Input
                  id="primaryInstrument"
                  value={primaryInstrument}
                  onChange={(e) => setPrimaryInstrument(e.target.value)}
                  placeholder="e.g. Guitar, Vocals, Drums"
                  className="h-12 bg-secondary border-border"
                  data-testid="primary-instrument-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Experience Level *</Label>
                <Select value={experienceLevel} onValueChange={setExperienceLevel}>
                  <SelectTrigger className="h-12 bg-secondary border-border" data-testid="experience-level-select">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE.musician.map((level) => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional Instruments */}
            <div className="space-y-2">
              <Label className="text-foreground">Additional Instruments (max 5)</Label>
              <div className="flex gap-2">
                <Input
                  value={newInstrument}
                  onChange={(e) => setNewInstrument(e.target.value)}
                  placeholder="Add instrument"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newInstrument, instruments, setInstruments, setNewInstrument, 5)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newInstrument, instruments, setInstruments, setNewInstrument, 5)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {instruments.map((inst) => (
                  <Badge key={inst} variant="secondary" className="gap-1 bg-secondary border-border">
                    {inst}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeTag(inst, instruments, setInstruments)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Genres */}
            <div className="space-y-2">
              <Label className="text-foreground">Genres (max 5)</Label>
              <div className="flex gap-2">
                <Input
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  placeholder="Add genre"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newGenre, genres, setGenres, setNewGenre, 5)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newGenre, genres, setGenres, setNewGenre, 5)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {genres.map((genre) => (
                  <Badge key={genre} variant="secondary" className="gap-1 bg-secondary border-border">
                    {genre}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(genre, genres, setGenres)} />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Event Types */}
            <div className="space-y-2">
              <Label className="text-foreground">Event Types (max 5)</Label>
              <div className="flex gap-2">
                <Input
                  value={newEventType}
                  onChange={(e) => setNewEventType(e.target.value)}
                  placeholder="e.g. Wedding, Corporate, Festival"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newEventType, eventTypes, setEventTypes, setNewEventType, 5)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newEventType, eventTypes, setEventTypes, setNewEventType, 5)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {eventTypes.map((type) => (
                  <Badge key={type} variant="secondary" className="gap-1 bg-secondary border-border">
                    {type}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(type, eventTypes, setEventTypes)} />
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rates & Travel */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Rates & Availability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Rate Type *</Label>
                <Select value={rateType} onValueChange={setRateType}>
                  <SelectTrigger className="h-12 bg-secondary border-border" data-testid="rate-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_TYPES.musician.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="baseRate" className="text-foreground">Base Rate (EUR) *</Label>
                <Input
                  id="baseRate"
                  type="number"
                  value={baseRate}
                  onChange={(e) => setBaseRate(e.target.value)}
                  placeholder="150"
                  className="h-12 bg-secondary border-border"
                  data-testid="base-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="travelRadiusKm" className="text-foreground">Travel Radius (km) *</Label>
                <Input
                  id="travelRadiusKm"
                  type="number"
                  value={travelRadiusKm}
                  onChange={(e) => setTravelRadiusKm(e.target.value)}
                  placeholder="50"
                  className="h-12 bg-secondary border-border"
                  data-testid="travel-radius-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="beyondRadiusRatePerKm" className="text-foreground">Beyond Radius Rate (EUR/km)</Label>
              <Input
                id="beyondRadiusRatePerKm"
                type="number"
                step="0.1"
                value={beyondRadiusRatePerKm}
                onChange={(e) => setBeyondRadiusRatePerKm(e.target.value)}
                placeholder="0.50"
                className="h-12 bg-secondary border-border"
              />
            </div>
          </CardContent>
        </Card>

        {/* Languages & Gear */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Languages & Gear</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Languages */}
            <div className="space-y-2">
              <Label className="text-foreground">Languages (max 5)</Label>
              <div className="flex gap-2">
                <Input
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  placeholder="e.g. English, Dutch"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newLanguage, languages, setLanguages, setNewLanguage, 5)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newLanguage, languages, setLanguages, setNewLanguage, 5)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <Badge key={lang} variant="secondary" className="gap-1 bg-secondary border-border">
                    {lang}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(lang, languages, setLanguages)} />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Gear */}
            <div className="space-y-2">
              <Label className="text-foreground">Gear List (max 10)</Label>
              <div className="flex gap-2">
                <Input
                  value={newGear}
                  onChange={(e) => setNewGear(e.target.value)}
                  placeholder="e.g. Fender Stratocaster"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newGear, gearList, setGearList, setNewGear, 10)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() => addTag(newGear, gearList, setGearList, setNewGear, 10)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {gearList.map((gear) => (
                  <Badge key={gear} variant="outline" className="gap-1 border-border">
                    {gear}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(gear, gearList, setGearList)} />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Gear I bring — structured gear declaration (per category) */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div>
                <Label className="text-foreground">Gear I Bring</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Check the gear you bring to gigs. Venues filter by this; you can override it per gig.
                </p>
              </div>
              <GearEditor value={gearProfile} onChange={setGearProfile} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riders Section */}
      <div className="mt-8">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-primary" />
              Technical Riders
            </CardTitle>
            <p className="text-sm text-muted-foreground">Upload your technical rider, stage plot, input list, or hospitality rider. Venues will see these when reviewing your profile.</p>
          </CardHeader>
          <CardContent>
            <RiderUpload profileId={user?.profileId} editable />
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Or build a <span className="text-foreground">structured rider</span> — equipment lines Stage8 can
                check against venue inventories automatically.
              </p>
              <Link
                to="/musicians/me/rider"
                className="inline-flex items-center gap-1.5 text-sm mt-2 hover:opacity-80 transition-opacity"
                style={{ color: 'var(--s8-amber, #C8923E)' }}
                data-testid="structured-rider-link"
              >
                Open rider builder →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="mt-8 pb-8">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="save-profile-btn"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
    </div>
  );
}
